import { type ReactNode } from 'react';
import { isAxiosError } from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  ADDRESS_FIELDS,
  ADDRESS_LABEL,
  ADDRESS_LABEL_KEY,
  type FiscalAddress,
} from '@/hooks/use-fiscal-registration';
import { useT } from '@/i18n/use-t';
import { cn } from '@/lib/utils';
import type { FieldErrors } from './fiscal-validation';

/** PFC-10 — the small form building blocks the registration forms share. */

type T = (key: string, fallback: string) => string;

export function FieldError({ errors, name }: { errors: FieldErrors; name: string }) {
  const t = useT('admin_ui');
  const error = errors[name];
  if (error === undefined) return null;
  return (
    <p role="alert" className="mt-1 text-xs text-destructive">
      {error.key === '' ? error.en : t(error.key, error.en)}
    </p>
  );
}

export function Field({
  id,
  label,
  errors,
  name,
  className,
  children,
}: {
  id: string;
  label: string;
  errors: FieldErrors;
  name: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <Label htmlFor={id}>{label}</Label>
      <div className="mt-1">{children}</div>
      <FieldError errors={errors} name={name} />
    </div>
  );
}

export function TextInput({
  id,
  value,
  onChange,
  disabled,
  invalid,
  mono,
  placeholder,
  maxLength,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  mono?: boolean;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <Input
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-invalid={invalid === true ? true : undefined}
      placeholder={placeholder}
      maxLength={maxLength}
      className={cn(mono === true && 'font-mono', invalid === true && 'border-destructive')}
    />
  );
}

export function SelectInput({
  id,
  value,
  onChange,
  disabled,
  children,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      {children}
    </Select>
  );
}

/** Address as strings for editing; `null` is shown as empty. */
export type AddressDraft = Record<keyof FiscalAddress, string>;

export function addressDraft(address: FiscalAddress | null): AddressDraft {
  const draft = {} as AddressDraft;
  for (const field of ADDRESS_FIELDS) draft[field] = address?.[field] ?? '';
  return draft;
}

export function addressBody(draft: AddressDraft): FiscalAddress {
  const body = {} as FiscalAddress;
  for (const field of ADDRESS_FIELDS) {
    const trimmed = draft[field].trim();
    body[field] = trimmed === '' ? null : trimmed;
  }
  return body;
}

export function AddressFields({
  idPrefix,
  value,
  onChange,
  errors,
  disabled,
}: {
  idPrefix: string;
  value: AddressDraft;
  onChange: (next: AddressDraft) => void;
  errors: FieldErrors;
  disabled?: boolean;
}) {
  const t = useT('admin_ui');
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {ADDRESS_FIELDS.map((field) => {
        const id = `${idPrefix}-address-${field}`;
        const wide = field === 'settlement' || field === 'street' || field === 'namedObject';
        return (
          <Field
            key={field}
            id={id}
            label={t(ADDRESS_LABEL_KEY[field], ADDRESS_LABEL[field])}
            errors={errors}
            name={`address.${field}`}
            className={wide ? 'col-span-2' : undefined}
          >
            <TextInput
              id={id}
              value={value[field]}
              onChange={(next) => onChange({ ...value, [field]: next })}
              disabled={disabled}
              invalid={errors[`address.${field}`] !== undefined}
            />
          </Field>
        );
      })}
    </div>
  );
}

/**
 * The API's refusal as a banner, plus per-field messages when it named
 * fields (a 400 from the validation pipe carries `issues[].path`).
 */
export function serverError(err: unknown, t: T): { banner: string; fields: FieldErrors } {
  const fields: FieldErrors = {};
  if (isAxiosError(err)) {
    const body = err.response?.data as
      | { message?: string | string[]; code?: string; issues?: { path: string; message: string }[] }
      | undefined;
    for (const issue of body?.issues ?? []) {
      if (issue.path !== '' && fields[issue.path] === undefined) {
        // The server's own wording (no key): the client checks normally catch
        // these first, in the operator's language.
        fields[issue.path] = { key: '', en: issue.message };
      }
    }
    const message = Array.isArray(body?.message) ? body?.message.join('; ') : body?.message;
    if (typeof message === 'string' && message !== '') {
      return { banner: message, fields };
    }
  }
  return { banner: t('fiscalCabinet.saveFailed', 'Could not save. Try again.'), fields };
}

export function Banner({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
      {children}
    </p>
  );
}
